import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { WhatsappLogo, EnvelopeSimple, Phone, MapPin, FacebookLogo, InstagramLogo, LinkedinLogo, TwitterLogo } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Footer() {
  const [siteSettings, setSiteSettings] = useState(null);
  const [services, setServices] = useState([]);

  useEffect(() => {
    fetchSiteSettings();
    fetchServices();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const res = await fetch(`${API}/site-settings`);
      if (res.ok) setSiteSettings(await res.json());
    } catch (error) {
      console.error('Error fetching site settings:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const res = await fetch(`${API}/services-list`);
      if (res.ok) setServices(await res.json());
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  // Get values from settings or use defaults
  const siteName = siteSettings?.site_name || 'SP BD Group';
  const contact = siteSettings?.contact || {};
  const social = siteSettings?.social || {};
  const footer = siteSettings?.footer || {};
  const logoUrl = siteSettings?.logo_url || 'https://customer-assets.emergentagent.com/job_aspbd-central/artifacts/fg24f6g6_SP%20BD%20GROUP%20logo.png';
  const displayLogoUrl = logoUrl.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${logoUrl}` : logoUrl;

  const whatsappNumber = contact.whatsapp || contact.phone || "+880 1234-567890";
  const whatsappMessage = `Hello ${siteName}, I have an inquiry...`;
  const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(whatsappMessage)}`;

  const visibleServices = services.filter(s => s.visible);

  return (
    <footer className="bg-gray-900 text-white py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          <div>
            <img 
              src={displayLogoUrl} 
              alt={siteName} 
              className="h-14 w-auto mb-4"
              data-testid="footer-logo"
            />
            <p className="text-sm text-gray-400 leading-relaxed">
              {siteSettings?.tagline || 'A diversified multi-business group serving excellence across hospitality, real estate, finance, and legal advocacy.'}
            </p>
            
            {/* Social Links */}
            <div className="flex space-x-3 mt-4">
              {social.facebook && (
                <a href={social.facebook} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-800 rounded-full hover:bg-[#6BBE45] transition" data-testid="footer-facebook">
                  <FacebookLogo size={18} />
                </a>
              )}
              {social.instagram && (
                <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-800 rounded-full hover:bg-[#6BBE45] transition" data-testid="footer-instagram">
                  <InstagramLogo size={18} />
                </a>
              )}
              {social.linkedin && (
                <a href={social.linkedin} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-800 rounded-full hover:bg-[#6BBE45] transition" data-testid="footer-linkedin">
                  <LinkedinLogo size={18} />
                </a>
              )}
              {social.twitter && (
                <a href={social.twitter} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-800 rounded-full hover:bg-[#6BBE45] transition" data-testid="footer-twitter">
                  <TwitterLogo size={18} />
                </a>
              )}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-bold tracking-[0.2em] uppercase mb-4">Services</h4>
            <div className="space-y-2">
              {visibleServices.map((service) => (
                <Link 
                  key={service.service_id}
                  to={service.path} 
                  className="block text-sm text-gray-400 hover:text-[#6BBE45] transition" 
                  data-testid={`footer-${service.service_id}-link`}
                >
                  {service.name}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-bold tracking-[0.2em] uppercase mb-4">Company</h4>
            <div className="space-y-2">
              <Link to="/blog" className="block text-sm text-gray-400 hover:text-[#6BBE45] transition" data-testid="footer-blog-link">Blog</Link>
              <Link to="/contact" className="block text-sm text-gray-400 hover:text-[#6BBE45] transition" data-testid="footer-contact-link">Contact</Link>
              <Link to="/services" className="block text-sm text-gray-400 hover:text-[#6BBE45] transition" data-testid="footer-pricing-link">Pricing</Link>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-bold tracking-[0.2em] uppercase mb-4">Contact</h4>
            <div className="space-y-3">
              {contact.address && (
                <div className="flex items-start space-x-2">
                  <MapPin size={18} className="text-gray-400 mt-1 flex-shrink-0" />
                  <span className="text-sm text-gray-400" data-testid="footer-address">{contact.address}</span>
                </div>
              )}
              {contact.phone && (
                <div className="flex items-center space-x-2">
                  <Phone size={18} className="text-gray-400" />
                  <a href={`tel:${contact.phone.replace(/\s/g, '')}`} className="text-sm text-gray-400 hover:text-[#6BBE45] transition" data-testid="footer-phone">{contact.phone}</a>
                </div>
              )}
              {contact.email && (
                <div className="flex items-center space-x-2">
                  <EnvelopeSimple size={18} className="text-gray-400" />
                  <a href={`mailto:${contact.email}`} className="text-sm text-gray-400 hover:text-[#6BBE45] transition" data-testid="footer-email">{contact.email}</a>
                </div>
              )}
              {(contact.whatsapp || contact.phone) && (
                <a 
                  href={whatsappUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 mt-4 px-4 py-2 bg-[#6BBE45] text-white rounded-sm hover:bg-[#5AA838] transition"
                  data-testid="footer-whatsapp-button"
                >
                  <WhatsappLogo size={20} weight="fill" />
                  <span className="text-sm font-bold">WhatsApp Us</span>
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8">
          <p className="text-center text-sm text-gray-500" data-testid="footer-copyright">
            {footer.copyright_text || `© ${new Date().getFullYear()} ${siteName}. All rights reserved.`}
          </p>
          {footer.credits && (
            <p className="text-center text-sm text-gray-600 mt-2" data-testid="footer-credits">
              Developed by <span className="text-[#6BBE45] font-bold">{footer.credits}</span>
            </p>
          )}
        </div>
      </div>
    </footer>
  );
}
